import React, { useState, useEffect } from 'react';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Table, TableBody, TableCell, TableHead, TableRow, Typography, Paper,
    Stack, MenuItem, CircularProgress, IconButton, Card, InputAdornment, Grid, TablePagination
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { setDoc, doc, getDocs, collection, updateDoc, deleteDoc, query, where, getDoc, orderBy } from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import { Close as CloseIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

const Feeheads = () => {
    const uid = auth.currentUser?.uid;
    const [schoolId, setSchoolId] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [feeheads, setFeeheads] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const initialForm = { fee_heads: '', short_head: '', sequence_no: '', installment: 'No', schoolId: '' };
    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);

    const fetchData = async (sId) => {
        const q = query(collection(db, 'feeheads'), where('schoolId', '==', sId), orderBy('sequence_no', 'asc'));
        const snap = await getDocs(q);
        setFeeheads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    useEffect(() => {
        if (!uid) return;
        getDoc(doc(db, 'users', uid)).then(userDoc => {
            if (userDoc.exists()) {
                const sId = userDoc.data().schoolId;
                setSchoolId(sId);
                fetchData(sId);
            }
        });
    }, [uid]);

    const handleClose = () => {
        setOpen(false);
        setFormData({ ...initialForm, schoolId });
        setEditId(null);
    };

    const handleSubmit = async () => {
        if (!formData.fee_heads || !formData.short_head) return toast.error("Required fields missing");
        setLoading(true);
        try {
            const data = { ...formData, sequence_no: Number(formData.sequence_no) || 0, schoolId, updatedAt: new Date() };
            if (editId) {
                await updateDoc(doc(db, "feeheads", editId), data);
            } else {
                const id = uuidv4();
                await setDoc(doc(db, "feeheads", id), { ...data, createdAt: new Date() });
            }
            fetchData(schoolId);
            handleClose();
            toast.success("Saved successfully");
        } catch (err) { toast.error(err.message); }
        setLoading(false);
    };

    const filtered = feeheads.filter(f => f.fee_heads.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <Box p={1}>
            <Stack direction="row" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Fee Heads</Typography>
                <Button variant="contained" onClick={() => setOpen(true)}>Add New</Button>
            </Stack>

            <Card>
                <TextField size="small" placeholder="Search..." sx={{ m: 2 }}
                    onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                />
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Fee Head</TableCell>
                            <TableCell>Short</TableCell>
                            <TableCell>Inst.</TableCell>
                            <TableCell>Seq</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, i) => (
                            <TableRow key={item.id}>
                                <TableCell>{i + 1 + page * rowsPerPage}</TableCell>
                                <TableCell>{item.fee_heads}</TableCell>
                                <TableCell>{item.short_head}</TableCell>
                                <TableCell>{item.installment}</TableCell>
                                <TableCell>{item.sequence_no}</TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => { setEditId(item.id); setFormData(item); setOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" color="error" onClick={async () => { if (window.confirm('Delete?')) { await deleteDoc(doc(db, 'feeheads', item.id)); fetchData(schoolId); } }}><DeleteIcon fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination component="div" count={filtered.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={e => setRowsPerPage(e.target.value)} />
            </Card>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
                <DialogTitle>{editId ? 'Edit' : 'Add'} Fee Head</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField fullWidth size="small" label="Fee Head" value={formData.fee_heads} onChange={e => setFormData({ ...formData, fee_heads: e.target.value })} />
                        <TextField fullWidth size="small" label="Short Head" value={formData.short_head} onChange={e => setFormData({ ...formData, short_head: e.target.value })} />
                        <TextField select fullWidth size="small" label="Installment" value={formData.installment} onChange={e => setFormData({ ...formData, installment: e.target.value })}>
                            <MenuItem value="Yes">Yes</MenuItem><MenuItem value="No">No</MenuItem>
                        </TextField>
                        <TextField fullWidth size="small" type="number" label="Seq" value={formData.sequence_no} onChange={e => setFormData({ ...formData, sequence_no: e.target.value })} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={loading}>{loading ? <CircularProgress size={20} /> : 'Save'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Feeheads;